import React, { useCallback, useMemo, useState } from 'react';
import { createContext } from '../dissolve/utils';
import { Offer, Product } from '../types';
import { getCart } from '../shared/api/shared-app-requests';
import { logError, logInfo } from '../shared/utils/logging';
import { REGISTERED_APPS } from '../shared/types/Apps';
import { Cart, CartItem } from '@digismoothie/shared-candyrack';

export function productOfferNotParentProduct(
  offer: Offer,
  parentProduct?: Product,
) {
  const isParentProduct =
    offer.hide_upsell_product_already_in_cart &&
    parentProduct &&
    offer.items.some((item) => parentProduct.handle === item.item_handle);
  logInfo(
    `Offer with offerId: ${offer.id}, isParentProduct: ${isParentProduct}`,
  );
  return !isParentProduct;
}

export function filterOfferItemsNotInCart(
  offer: Offer,
  cartResponse: Cart,
): Offer {
  if (!offer.hide_upsell_product_already_in_cart) {
    return offer;
  }

  const handlesInCart = cartResponse.items.flatMap((item: CartItem) => [
    item.handle,
  ]);
  const variantIdsInCart = cartResponse.items.flatMap((item: CartItem) => [
    item.variant_id,
  ]);

  const filteredItems = offer.items.filter(
    (item) =>
      !handlesInCart.includes(item.item_handle) &&
      !variantIdsInCart.some((variantId) =>
        item.item_variants.includes(variantId),
      ),
  );

  logInfo(
    `Offer ${offer.id}: Filtered ${
      offer.items.length - filteredItems.length
    } items that are already in cart`,
  );

  return {
    ...offer,
    items: filteredItems,
  };
}

export interface CandyRackContextShape {
  parentProduct?: Product;
  setParentProduct: (product: Product) => void;
  parentProductVariantId?: number;
  setParentProductVariantId: (id: number) => void;
  resetParentProductVariantId: () => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
  upgradeOffer?: Offer;
  setUpgradeOffer: (offer: Offer) => void;
  resetUpgradeOffer: () => void;
  removeOffersWithProductAlreadyInCart: (offers: Offer[]) => Promise<Offer[]>;
  storefrontAccessToken?: string;
  useDSRecommEngine?: boolean;
}

const [useCandyRackContext, ContextProvider, ContextConsumer] =
  createContext<CandyRackContextShape>();

interface Props {
  children: React.ReactNode;
  storefrontAccessToken?: string;
  useDSRecommEngine?: boolean;
}

const CandyRackContextProvider = ({
  children,
  storefrontAccessToken,
  useDSRecommEngine,
}: Props) => {
  const [parentProduct, setParentProduct] = useState<Product>();
  const [parentProductVariantId, setParentProductVariantId] =
    useState<number>();
  const [quantity, setQuantity] = useState(0);
  const [upgradeOffer, setUpgradeOffer] = useState<Offer>();

  const resetParentProductVariantId = () => {
    setParentProductVariantId(undefined);
  };

  const resetUpgradeOffer = () => {
    setUpgradeOffer(undefined);
  };

  const removeOffersWithProductAlreadyInCart = useCallback(
    async (offers: Offer[]) => {
      try {
        logInfo(
          `removeOffersWithProductAlreadyInCart - Original offers [${offers
            ?.map((offer) => offer.id)
            .join(',')}]`,
          REGISTERED_APPS.ADD_TO_CART,
        );

        // Separate same product offers from regular offers
        const sameProductOffers = offers.filter(
          (offer) => offer.is_same_product_offer,
        );
        const regularOffers = offers.filter(
          (offer) => !offer.is_same_product_offer,
        );

        // Check if any regular offers have the hide_upsell_product_already_in_cart flag
        const hasHideFlag = regularOffers.some(
          (offer) => offer.hide_upsell_product_already_in_cart,
        );

        if (!hasHideFlag) {
          logInfo(
            'No offer limitation about hiding products already in cart, returning all offers as is.',
          );
          return offers;
        }

        const cartResponse = await getCart();
        // Process regular offers with filtering
        const filteredRegularOffers = regularOffers
          .filter((offer) => productOfferNotParentProduct(offer, parentProduct))
          .map((offer) => filterOfferItemsNotInCart(offer, cartResponse))
          .filter(
            (offer) => offer.items.length > 0 || offer.use_recommended_products,
          ); // Only keep offers that still have items or recomended products

        // Combine same product offers (unfiltered) with filtered regular offers
        const allFilteredOffers = [
          ...sameProductOffers,
          ...filteredRegularOffers,
        ];

        logInfo(
          `removeOffersWithProductAlreadyInCart - Same product offers: [${sameProductOffers
            .map((offer) => offer.id)
            .join(',')}], Filtered regular offers: [${filteredRegularOffers
            .map((offer) => offer.id)
            .join(',')}], Total: [${allFilteredOffers
            .map((offer) => offer.id)
            .join(',')}]`,
        );
        return allFilteredOffers;
      } catch (error) {
        logError('Failed to load cart', REGISTERED_APPS.ADD_TO_CART, error);
        return offers;
      }
    },
    [parentProduct],
  );

  const providerValue: CandyRackContextShape = useMemo(
    () => ({
      parentProduct,
      setParentProduct,
      parentProductVariantId,
      setParentProductVariantId,
      resetParentProductVariantId,
      quantity,
      setQuantity,
      upgradeOffer,
      setUpgradeOffer,
      resetUpgradeOffer,
      removeOffersWithProductAlreadyInCart,
      storefrontAccessToken,
      useDSRecommEngine,
    }),
    [
      parentProduct,
      parentProductVariantId,
      quantity,
      upgradeOffer,
      removeOffersWithProductAlreadyInCart,
      storefrontAccessToken,
      useDSRecommEngine,
    ],
  );

  return <ContextProvider value={providerValue}>{children}</ContextProvider>;
};

const CandyRackContextConsumer =
  ContextConsumer as React.Consumer<CandyRackContextShape>;

export {
  useCandyRackContext,
  CandyRackContextProvider,
  CandyRackContextConsumer,
};
